<div class="page" [nbSpinner]="loading">
  <div class="header">
    <div class="al-page-title">
      <a
        class="al-page-back al-relief-icon al-relief-icon--large al-icon--chevron-left"
        routerLink="/agent-setup/skills"
        [attr.aria-label]="'buttons.back' | translate"
        [nbTooltip]="'buttons.back' | translate"
        nbTooltipPlacement="right"
      ><span class="al-relief-glyph" aria-hidden="true"></span></a>
      <h2>{{ isNew ? ("pages.skills.editor.newTitle" | translate) : (skill?.title || skill?.name) }}</h2>
    </div>
  </div>

  <nb-card [nbSpinner]="saving">
    <nb-card-body>
      <!-- An import is read before it is used: report first. -->
      <section class="report" *ngIf="skill?.import_report as r">
        <strong>{{ "pages.skills.report.title" | translate: { file: skill?.origin_filename || "" } }}</strong>
        <p *ngIf="skill?.origin_url">
          {{ "pages.skills.report.origin" | translate }}
          <a [href]="skill?.origin_url" target="_blank" rel="noopener noreferrer">{{ skill?.origin_url }}</a>
          <span *ngIf="skill?.origin_license"> · {{ "pages.skills.report.license" | translate: { license: skill?.origin_license } }}</span>
          <span *ngIf="skill?.origin_ref"> · {{ "pages.skills.report.commit" | translate: { commit: (skill?.origin_ref || "").slice(0, 7) } }}</span>
        </p>
        <p *ngIf="needsReview">{{ "pages.skills.report.review" | translate }}</p>
        <p *ngIf="r.scripts.length">{{ "pages.skills.report.scripts" | translate: { files: r.scripts.join(", ") } }}</p>
        <p *ngIf="r.credentials?.length" class="report__credentials">
          {{ "pages.skills.report.credentials" | translate: { names: r.credentials!.join(", ") } }}
        </p>
        <p *ngIf="r.warnings.includes('allowed_tools_not_enforced')">{{ "pages.skills.report.allowedTools" | translate }}</p>
        <p *ngIf="r.warnings.includes('description_shortened')">{{ "pages.skills.report.descriptionShortened" | translate }}</p>
        <p *ngIf="r.rejected.length">
          {{ "pages.skills.report.rejected" | translate }}
          <span *ngFor="let x of r.rejected" class="rejected"><code>{{ x.path }}</code> ({{ x.reason }})</span>
        </p>
      </section>

      <form #f="ngForm" (ngSubmit)="save(f)" novalidate class="form">
        <div class="grid-2">
          <div class="form-field">
            <label class="label">
              {{ "pages.skills.fields.name" | translate }} *
              <nb-icon icon="question-mark-circle-outline" class="hint-icon"
                nbPopover="{{ 'pages.skills.hints.name' | translate }}" nbPopoverTrigger="hover"></nb-icon>
            </label>
            <input nbInput fullWidth fieldSize="small" name="name" required maxlength="64" [(ngModel)]="name"
              placeholder="reklamation-bearbeiten" />
          </div>
          <div class="form-field">
            <label class="label">
              {{ "pages.skills.fields.title" | translate }}
              <nb-icon icon="question-mark-circle-outline" class="hint-icon"
                nbPopover="{{ 'pages.skills.hints.title' | translate }}" nbPopoverTrigger="hover"></nb-icon>
            </label>
            <input nbInput fullWidth fieldSize="small" name="title" maxlength="200" [(ngModel)]="title" />
          </div>
        </div>

        <div class="form-field">
          <label class="label">
            {{ "pages.skills.fields.description" | translate }} *
            <nb-icon icon="question-mark-circle-outline" class="hint-icon"
              nbPopover="{{ 'pages.skills.hints.description' | translate }}" nbPopoverTrigger="hover"></nb-icon>
          </label>
          <textarea nbInput fullWidth rows="2" name="description" required [maxlength]="descriptionMax"
            [(ngModel)]="description" [placeholder]="'pages.skills.fields.descriptionPlaceholder' | translate"></textarea>
          <span class="hint">{{ description.length }} / {{ descriptionMax }}</span>
        </div>

        <div class="form-field">
          <label class="label">
            {{ "pages.skills.fields.body" | translate }}
            <nb-icon icon="question-mark-circle-outline" class="hint-icon"
              nbPopover="{{ 'pages.skills.hints.body' | translate }}" nbPopoverTrigger="hover"></nb-icon>
          </label>
          <textarea nbInput fullWidth rows="16" name="body" class="mono" data-enter-ignore [(ngModel)]="body"
            [placeholder]="'pages.skills.fields.bodyPlaceholder' | translate"></textarea>
        </div>

        <div class="row">
          <nb-toggle name="is_active" [(ngModel)]="isActive"
            [nbTooltip]="(needsReview ? 'pages.skills.tooltips.activateImport' : 'pages.skills.tooltips.active') | translate">
            {{ (isActive ? "pages.skills.status.active" : "pages.skills.status.inactive") | translate }}
          </nb-toggle>
          <button nbButton status="primary" size="small" type="submit" [disabled]="saving">
            {{ "buttons.save" | translate }}
          </button>
        </div>
      </form>

      <ng-container *ngIf="skill">
        <section class="tile-flat">
          <header class="tile-flat__header">
            <h5>
              {{ "pages.skills.editor.filesTitle" | translate: { count: skill.files.length } }}
              <nb-icon icon="question-mark-circle-outline" class="hint-icon"
                nbPopover="{{ 'pages.skills.hints.files' | translate }}" nbPopoverTrigger="hover"></nb-icon>
            </h5>
          </header>
          <ul class="files">
            <li *ngFor="let file of skill.files; trackBy: trackFile">
              <div class="file-head">
                <button type="button" class="link" (click)="toggleFile(file)"
                  [nbTooltip]="'pages.skills.tooltips.showFile' | translate">
                  <code>{{ file.rel_path }}</code>
                </button>
                <span class="muted">{{ file.size_bytes | number }} B</span>
                <span class="badge" *ngIf="file.is_executable_stub"
                  [nbTooltip]="'pages.skills.tooltips.script' | translate">
                  {{ "pages.skills.editor.script" | translate }}
                </span>
                <button nbButton ghost size="tiny" status="basic" type="button" (click)="deleteFile(file)"
                  [nbTooltip]="'buttons.delete' | translate">
                  <nb-icon icon="trash-outline"></nb-icon>
                </button>
              </div>
              <pre *ngIf="openFile[file.id] !== undefined">{{ openFile[file.id] ?? "…" }}</pre>
            </li>
          </ul>
          <div class="add-file">
            <input nbInput fieldSize="small" [(ngModel)]="newFilePath" [ngModelOptions]="{ standalone: true }"
              placeholder="references/rubrik.md" data-enter-ignore
              [nbTooltip]="'pages.skills.tooltips.filePath' | translate" />
            <textarea nbInput fullWidth rows="3" class="mono" [(ngModel)]="newFileContent"
              [ngModelOptions]="{ standalone: true }" data-enter-ignore
              [placeholder]="'pages.skills.editor.fileContent' | translate"></textarea>
            <button nbButton size="small" status="basic" type="button" (click)="addFile()"
              [nbTooltip]="'pages.skills.tooltips.addFile' | translate">
              <nb-icon icon="plus-outline"></nb-icon>{{ "pages.skills.editor.addFile" | translate }}
            </button>
          </div>
        </section>

        <section class="tile-flat">
          <header class="tile-flat__header">
            <h5>
              {{ "pages.skills.editor.agentsTitle" | translate }}
              <nb-icon icon="question-mark-circle-outline" class="hint-icon"
                nbPopover="{{ 'pages.skills.hints.agents' | translate: { max: maxPerAgent } }}" nbPopoverTrigger="hover"></nb-icon>
            </h5>
          </header>
          <div class="agents">
            <nb-checkbox *ngFor="let a of agents; trackBy: trackAgent" [checked]="a.assigned"
              [nbTooltip]="'pages.skills.tooltips.assign' | translate"
              (checkedChange)="toggleAgent(a, $event)">{{ a.name }}</nb-checkbox>
          </div>
          <p class="hint">{{ "pages.skills.editor.agentsHint" | translate }}</p>
        </section>
      </ng-container>
    </nb-card-body>
  </nb-card>
</div>
